/* eslint-disable no-await-in-loop, no-restricted-syntax, no-nested-ternary */

import Vue from 'vue';
import camelCase from 'camelcase';
import { isVite } from '@/utils/env';

let serviceFiles = null;

if (isVite()) {
    // Jeśli Vite
    serviceFiles = import.meta.glob('../services/*.js');
} else {
    // Jeśli Webpack
    serviceFiles = require.context('../services', false, /\.js$/);
}

/**
 * Ładuje serwis dynamicznie na podstawie nazwy modułu.
 * @param {string} moduleName - Nazwa modułu.
 * @returns {Promise<object>} Serwis modułu.
 * @throws {Error} Jeśli nie znaleziono serwisu dla modułu.
 */
async function loadService(moduleName) {
    if (isVite()) {
        // Dla Vite iterujemy po kluczach obiektu serviceFiles
        for (const path in serviceFiles) {
            if (`./${moduleName}Service.js` === path) {
                const serviceFile = await serviceFiles[path]();
                return serviceFile;
            }
        }
    } else {
        // Dla Webpacka używamy keys()
        const keys = serviceFiles.keys();
        for (let i = 0; i < keys.length; i++) {
            const path = keys[i];
            if (`./${moduleName}Service.js` === path) {
                const serviceFile = await serviceFiles(path);
                return serviceFile;
            }
        }
    }
    throw new Error(`Service for module ${moduleName} not found.`);
}

const DEFAULT_ID = 'DEFAULT';

// Metody HTTP, które chcemy obsługiwać
const httpMethods = ['get', 'put', 'post', 'patch', 'delete'];

/**
 * Klasa RequestBuilder umożliwia tworzenie i zarządzanie zapytaniami do serwisów.
 */
class RequestBuilder {
    /**
     * Tworzy instancję RequestBuilder.
     * @param {string} moduleAndEndpoint - Połączenie nazwy modułu i endpointu.
     */
    constructor(moduleAndEndpoint) {
        // Dynamicznie dodajemy metody do RequestBuilder
        httpMethods.forEach(method => {
            this[method] = (...payload) => this.execute(method, ...payload);
        });

        this.moduleAndEndpoint = moduleAndEndpoint;
        // Stan zapytań; każdy klucz (id) zawiera obiekt: { loading, error, data, abortController }
        this.state = Vue.observable({});
        this.currentId = DEFAULT_ID;
        this.serviceFile = null;

        // Wstępne ładowanie serwisu podczas inicjalizacji
        const [moduleName] = this.moduleAndEndpoint.split('/');
        this.servicePromise = loadService(moduleName)
            .then(service => {
                this.serviceFile = service;
                return service;
            })
            .catch(error => {
                console.error(`Failed to load service for module: ${moduleName}`, error);
                throw error;
            });
    }

    /**
     * Inicjalizuje stan zapytania dla danego identyfikatora.
     * @param {string} id - Identyfikator zapytania.
     */
    initializeState(id) {
        if (!this.state[id]) {
            Vue.set(this.state, id, {
                loading: false,
                error: null,
                data: null,
                abortController: null,
            });
        }
    }

    /**
     * Ustawia identyfikator zapytania.
     * @param {string} id - Identyfikator zapytania.
     * @returns {RequestBuilder} Instancja RequestBuilder.
     */
    id(id = DEFAULT_ID) {
        this.currentId = id;
        return this;
    }

    /**
     * Wykonuje zapytanie HTTP.
     * Tworzy indywidualny AbortController dla danego zapytania, zapisuje go w stanie,
     * a następnie przekazuje do wywołania funkcji serwisowej.
     * @param {string} method - Metoda HTTP.
     * @param {...any} payload - Parametry zapytania.
     * @returns {Promise<any>} Wynik zapytania.
     */
    async execute(method, ...payload) {
        const requestId = this.currentId;

        // Resetujemy currentId od razu, aby kolejne zapytania korzystały z DEFAULT_ID,
        // chyba że użytkownik jawnie ustawi nowe ID przed kolejnym wywołaniem.
        this.currentId = DEFAULT_ID;

        this.initializeState(requestId);

        if (this.state[requestId].loading) {
            this.abort(requestId);
        }

        this.state[requestId].abortController = new AbortController();
        this.state[requestId].loading = true;
        this.state[requestId].error = null;

        const [, endpoint] = this.moduleAndEndpoint.split('/');
        const methodName = camelCase(endpoint, { pascalCase: true });

        // Czeka na wstępnie załadowany serwis
        if (!this.serviceFile) {
            await this.servicePromise;
        }

        const service = this.serviceFile?.default;
        if (!service) {
            throw new Error('Service not loaded');
        }

        const serviceExecute = (...methods) => {
            for (const methodType of methods) {
                const fullMethodName = `${methodType}${methodName}`;
                if (typeof service[fullMethodName] === 'function') {
                    return service[fullMethodName];
                }
            }
            throw new Error(`No suitable method found for ${method}${methodName} in service.`);
        };

        const abortControllerForRequest = this.state[requestId].abortController;

        let response;
        try {
            // Przekazujemy indywidualny AbortController jako ostatni argument do funkcji serwisowej
            switch (method) {
                case 'get':
                    response = await serviceExecute('get', 'fetch')(...payload, abortControllerForRequest);
                    break;
                case 'put':
                    response = await serviceExecute('put')(...payload, abortControllerForRequest);
                    break;
                case 'post':
                    response = await serviceExecute('post')(...payload, abortControllerForRequest);
                    break;
                case 'patch':
                    response = await serviceExecute('patch', 'update')(...payload, abortControllerForRequest);
                    break;
                case 'delete':
                    response = await serviceExecute('delete')(...payload, abortControllerForRequest);
                    break;
                default:
                    throw new Error(`Unsupported method: ${method}`);
            }
            if (!abortControllerForRequest.signal.aborted) {
                this.state[requestId].error = null;
                this.state[requestId].data = response;
            }
            return this.state[requestId].data;
        } catch (error) {
            if (!abortControllerForRequest.signal.aborted) {
                this.state[requestId].data = null;
                this.state[requestId].error = error;
            }
            // Rzucamy błąd, aby dalej go obsłużył caller
            return Promise.reject(error);
        } finally {
            if (!abortControllerForRequest.signal.aborted) {
                this.state[requestId].loading = false;
            }
        }
    }

    /**
     * Resetuje stan zapytania.
     * @param {string} id - Opcjonalny identyfikator zapytania.
     */
    reset(id = DEFAULT_ID) {
        if (this.state[id]) {
            this.state[id].loading = false;
            this.state[id].error = null;
            this.state[id].data = null;
        }
        return this;
    }

    /**
     * Resetuje stan wszystkich zapytań.
     */
    resetAll() {
        Object.keys(this.state).forEach(id => this.reset(id));
    }

    /**
     * Pobiera stan zapytania.
     * @param {string} id - Opcjonalny identyfikator zapytania.
     * @returns {object} Stan zapytania.
     */
    getState(id = this.currentId) {
        return {
            loading: this.getLoading(id),
            error: this.getError(id),
            data: this.getData(id),
        };
    }

    /**
     * Pobiera stan `loading` zapytania.
     * @param {string} id - Opcjonalny identyfikator zapytania.
     * @returns {boolean} Stan `loading`.
     */
    getLoading(id = DEFAULT_ID) {
        return this.state[id] ? this.state[id].loading : false;
    }

    /**
     * Sprawdza, czy w dowolnym obiekcie w stanie jest loading ustawione na true.
     * @returns {boolean} True, jeśli loading jest ustawione na true w którymkolwiek obiekcie stanu.
     */
    getAnyLoading() {
        return Object.values(this.state).some(stateObj => stateObj.loading === true);
    }

    /**
     * Pobiera stan `error` zapytania.
     * @param {string} id - Opcjonalny identyfikator zapytania.
     * @returns {any} Stan `error`.
     */
    getError(id = DEFAULT_ID) {
        return this.state[id] ? this.state[id].error : null;
    }

    /**
     * Pobiera stan `data` zapytania.
     * @param {string} id - Opcjonalny identyfikator zapytania.
     * @returns {any} Stan `data`.
     */
    getData(id = DEFAULT_ID) {
        return this.state[id] ? this.state[id].data : null;
    }

    /**
     * Metoda abort anuluje indywidualne zapytanie o podanym identyfikatorze.
     * Jeśli nie podamy id, wykorzysta bieżące ustawione (this.currentId lub DEFAULT_ID).
     * @param {string} id - Opcjonalny identyfikator zapytania.
     * @returns {RequestBuilder} Instancja RequestBuilder dla łańcuchowania.
     */
    abort(id = DEFAULT_ID) {
        const reqState = this.state[id];
        if (reqState?.abortController) {
            reqState.abortController.abort();
            reqState.abortController = new AbortController();
            reqState.loading = false;
            this.currentId = DEFAULT_ID;
        }
        return this;
    }

    /**
     * Metoda abortAll anuluje wszystkie zapytania.
     */
    abortAll() {
        Object.keys(this.state).forEach(id => this.abort(id));
    }

    /**
     * Getters for loading, error, and data directly.
     */
    get loading() {
        return this.getLoading();
    }

    get error() {
        return this.getError();
    }

    get data() {
        return this.getData();
    }
}

/**
 * Tworzy instancję RequestBuilder dla zadanego modułu i endpointu.
 * @param {string} moduleAndEndpoint - Połączenie nazwy modułu i endpointu.
 * @returns {RequestBuilder} Instancja RequestBuilder.
 */
export default function createRequest(moduleAndEndpoint) {
    return new RequestBuilder(moduleAndEndpoint);
}

/* Użycie:

// Przykładowe użycie:
const requestBrands = createRequest('catalog/brands');

// Inicjujemy zapytania z unikalnymi identyfikatorami:
requestBrands.id('ID_1').get({ id: 1 });
requestBrands.id('ID_2').put({ id: 2 });

// Aby anulować indywidualne zapytanie (np. o id 'ID_1'):
requestBrands.abort('ID_1');

// Jeśli nie podamy id, metoda abort() użyje domyślnego identyfikatora.
*/
